import { Checkbox } from "./ui/checkbox";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import SearchSelect from "./SearchSelect";
import { api, errMsg } from "../lib/api";
import { useData } from "../lib/DataContext";
import { TierBadge } from "./shared";
import ResourceFields, { useMachines, resFromBom, resBody } from "./ResourceFields";

const ItemSelect = SearchSelect;

// mode: "new" | "edit" | "duplicate"
export default function BomEditor({ mode, bom, onClose, presetProductId, focusComponents }) {
  useEffect(() => { if (focusComponents) { const t = setTimeout(() => document.getElementById("bom-components-heading")?.scrollIntoView({ block: "start", behavior: "smooth" }), 250); return () => clearTimeout(t); } }, [focusComponents, bom?.id]);
  const { allItems, itemsById, boms, refreshAll } = useData();
  const usedIds = new Set((bom?.components || []).map((c) => c.item_id));
  const items = allItems.filter((i) => !i.inactive || usedIds.has(i.id)).map((i) => (i.inactive ? { ...i, name: `${i.name} (INACTIVE)` } : i));
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [linked, setLinked] = useState(true);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ptime, setPtime] = useState(0);
  const [prate, setPrate] = useState(0);
  const [res, setRes] = useState(resFromBom(null));
  const machines = useMachines();

  useEffect(() => {
    if (!mode) return;
    setProductId(mode === "edit" ? bom.product_id : presetProductId || "");
    setName(mode === "edit" ? bom.name : "");
    setLinked(mode === "edit" ? !!bom.name_linked : true);
    setPtime(bom?.production_time_minutes || 0);
    setPrate(bom?.piece_rate || 0);
    setRes(resFromBom(bom));
    setRows(bom ? bom.components.map((c) => ({ ...c, orig: c.item_id })) : [{ item_id: "", qty: 1 }]);
  }, [mode, bom, presetProductId]);
  if (!mode) return null;

  const taken = new Set(boms.filter((b) => b.id !== bom?.id || mode !== "edit").map((b) => b.product_id));
  const products = items.filter((i) => i.tier > 1 && !taken.has(i.id));
  const product = itemsById[productId];
  const compOptions = items.filter((i) => (i.tier < (product?.tier || 3) || (product?.tier === 2 && i.tier === 2)) && i.id !== product?.id);
  const sameCatOptions = (orig) => {
    const o = itemsById[orig];
    return o ? compOptions.filter((i) => i.category === o.category) : compOptions;
  };
  const linkedName = product ? `${product.name} BOM` : "";
  const setRow = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setBusy(true);
    const body = { product_id: productId, name: linked ? linkedName : (name || linkedName), name_linked: linked, needs_review: mode === "duplicate", production_time_minutes: Number(ptime) || 0, piece_rate: Number(prate) || 0, ...(product?.production_type === "external" ? {} : resBody(res)), components: rows.filter((r) => r.item_id).map(({ item_id, qty }) => ({ item_id, qty: Number(qty) })) };
    try {
      if (mode === "edit") await api.put(`/boms/${bom.id}`, body); else await api.post("/boms", body);
      toast.success(mode === "duplicate" ? "BOM duplicated" : "BOM saved");
      await refreshAll(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const title = { new: "New BOM", edit: "Edit BOM", duplicate: "Duplicate BOM" }[mode];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F1623] border-[#334155] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bom-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          {mode === "duplicate" && <div className="text-sm text-[#9CA3AF]">Copied from <span className="text-white">{bom.name}</span>. Pick the new product and swap variant materials below.</div>}
        </DialogHeader>
        {product?.production_type === "external" ? (
          <label className="flex flex-col gap-1 sm:w-1/2">
            <span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">OUTSOURCED PIECE RATE ($ / UNIT)</span>
            <input type="number" step="any" min="0" data-testid="bom-piece-rate-input" value={prate} onChange={(e) => setPrate(e.target.value)}
              className="no-spin h-12 px-3 font-mono bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]" />
            <span className="text-[11px] text-[#6B7280]">Product is externally produced. Unit cost = materials + piece rate.</span>
          </label>
        ) : (
        <div className="sm:w-2/3"><ResourceFields res={res} setRes={setRes} machines={machines} /></div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">TARGET PRODUCT</label>
            <ItemSelect value={productId} onChange={(v) => { setProductId(v); if (!name || mode !== "edit") setName(`${itemsById[v]?.name} BOM`); }}
              options={products} testId="bom-product-select" placeholder="Select product (Tier 2/3)" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">BOM NAME</label>
              <label className="flex items-center gap-2 text-[11px] text-[#9CA3AF] cursor-pointer">
                <Checkbox data-testid="bom-name-linked-checkbox" checked={linked} onCheckedChange={(v) => { setLinked(!!v); if (!v && !name) setName(linkedName); }} />
                Use product name
              </label>
            </div>
            <input data-testid="bom-name-input" value={linked ? linkedName : name} disabled={linked} onChange={(e) => setName(e.target.value)}
              placeholder={linked ? "Select a product" : ""}
              className="w-full h-12 px-3 bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B] disabled:opacity-60 disabled:cursor-not-allowed" />
            {linked && <span className="text-[11px] text-[#6B7280]" data-testid="bom-name-linked-hint">Follows the product name automatically.</span>}
          </div>
        </div>
        <div id="bom-components-heading" data-testid="bom-components-heading" className={`font-mono text-[10px] tracking-[0.2em] mt-2 ${focusComponents ? "text-[#F59E0B]" : "text-[#6B7280]"}`}>COMPONENTS · QTY PER UNIT</div>
        <div className="space-y-2">
          {rows.map((r, i) => {
            const orig = r.orig && itemsById[r.orig];
            const swapped = r.orig && r.orig !== r.item_id;
            return (
              <div key={i} data-testid={`bom-row-${i}`} className={`p-3 rounded-md border ${swapped ? "border-[#FF5722] bg-[#FF5722]/5" : "border-[#1E293B]"}`}>
                {orig && mode === "duplicate" && (
                  <div className="flex items-center gap-2 text-xs mb-2 text-[#9CA3AF]">
                    <TierBadge tier={orig.tier} /> <span className={swapped ? "line-through" : ""}>{orig.name}</span>
                    {swapped && <><ArrowRight className="w-3 h-3 text-[#FF5722]" /><span className="text-[#FF5722] font-semibold" data-testid={`bom-swap-label-${i}`}>{itemsById[r.item_id]?.name}</span></>}
                  </div>
                )}
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-12 sm:col-span-7">
                    <ItemSelect value={r.item_id} onChange={(v) => setRow(i, { item_id: v })}
                      options={mode === "duplicate" && r.orig ? sameCatOptions(r.orig) : compOptions}
                      testId={mode === "duplicate" ? `bom-swap-material-select-${i}` : `bom-component-select-${i}`} placeholder="Component" />
                  </div>
                  <input type="number" step="any" data-testid={`bom-qty-input-${i}`} value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })}
                    className="col-span-8 sm:col-span-3 h-12 px-3 font-mono bg-[#090D16] border border-[#334155] rounded-md text-right" />
                  <span className="col-span-2 sm:col-span-1 font-mono text-xs text-[#6B7280]">{itemsById[r.item_id]?.unit}</span>
                  <button data-testid={`bom-remove-row-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}
                    className="col-span-2 sm:col-span-1 h-12 flex items-center justify-center text-[#EF4444] hover:bg-[#EF4444]/10 rounded-md"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
        <button data-testid="bom-add-row-btn" onClick={() => setRows([...rows, { item_id: "", qty: 1 }])}
          className="h-12 rounded-md border border-dashed border-[#334155] text-[#9CA3AF] flex items-center justify-center gap-2 hover:text-white"><Plus className="w-4 h-4" />Add component</button>
        <button data-testid="bom-save-btn" disabled={busy || !productId} onClick={save}
          className="h-14 rounded-md bg-[#F59E0B] text-[#090D16] font-display font-extrabold text-lg disabled:opacity-40">{busy ? "SAVING…" : mode === "duplicate" ? "CREATE DUPLICATE" : "SAVE BOM"}</button>
      </DialogContent>
    </Dialog>
  );
}
